/**
 * McConsole -- Live console log viewer with Pterodactyl WebSocket streaming.
 *
 * Connects to the panel-minted wss:// WebSocket and displays live console
 * output in a terminal-styled panel. Supports auto-scroll with pause/resume,
 * collapse/expand, drag-to-resize, and search highlighting with match navigation.
 *
 * D-07: Read-only. Never sends 'send command' or 'set state'.
 * D-06: Connects to the panel-returned socket URL verbatim.
 * D-01: Live-updates CPU/RAM/uptime stat cards; disk stays cache-sourced.
 */

function escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Strip ANSI/VT terminal escape sequences from a live console line.
 * The Pterodactyl WS stream carries RAW terminal output (SGR colors like `\x1b[32m`,
 * erase-line `\x1b[K`, the `\x1b[m` reset, cursor moves) plus carriage returns / bells
 * from the server's JLine prompt redraw. The panel's xterm.js interprets these, but this
 * lightweight <pre> viewer would render them as literal garbage (and the leading escape
 * also breaks LOG_LINE_START detection, mis-coloring every line). Strip them so the
 * existing content-based colorizeLine() re-colors cleanly.
 */
// eslint-disable-next-line no-control-regex
const ANSI_ESCAPE = /\x1b\[[0-9;?]*[A-Za-z]|[\r\x07\x08]/g;
function stripAnsi(line: string): string {
    return line.replace(ANSI_ESCAPE, '');
}

/** Regex matching the start of a new MC log line: [HH:MM:SS] [Thread/LEVEL]: */
const LOG_LINE_START = /^\[[\d:]+\]\s*\[/;

/** Track the last color class so continuation lines inherit it. */
let lastColorClass: string | null = null;

/**
 * Colorize a single line of Minecraft server log output.
 * Matches common Fabric/Forge/Vanilla log patterns.
 * Continuation lines (no timestamp prefix) inherit the previous line's color.
 */
function colorizeLine(line: string): string {
    const escaped = escapeHtml(line);

    // Continuation line: no timestamp prefix → inherit previous color
    if (!LOG_LINE_START.test(line)) {
        if (lastColorClass) {
            return `<span class="${lastColorClass}">${escaped}</span>`;
        }
        return escaped;
    }

    // New log line — determine its color
    let cls: string | null = null;

    // Player join/leave messages
    if (/joined the game/i.test(line)) {
        cls = 'mc-log-join';
    } else if (/left the game/i.test(line)) {
        cls = 'mc-log-leave';
    }
    // Chat messages: <PlayerName> message
    else if (/\[.*?INFO.*?\]:.*<\w+>/.test(line) || /^<\w+>/.test(line)) {
        cls = 'mc-log-chat';
    }
    // Death messages (common patterns)
    else if (/\b(was slain by|was shot by|fell from|drowned|burned to death|blew up|hit the ground|was killed by|was pummeled by|suffocated|starved to death|withered away|was pricked to death|fell out of the world|went up in flames|walked into fire|tried to swim in lava|was squished|was blown up by|was fireballed by|was stung to death|was impaled by|was squashed by)\b/i.test(line)) {
        cls = 'mc-log-death';
    }
    // Advancement/challenge messages
    else if (/has (made the advancement|completed the challenge|reached the goal)/i.test(line)) {
        cls = 'mc-log-advancement';
    }
    // Server start/stop/reload
    else if (/\b(Starting minecraft server|Done \(\d|Stopping the server|Stopping server|Server thread\/INFO\]:\s*Starting|loaded \d+ recipes|Preparing level)/i.test(line)) {
        cls = 'mc-log-system';
    }
    // Error / FATAL level
    else if (/\/(ERROR|FATAL)\]:/.test(line)) {
        cls = 'mc-log-error';
    }
    // WARN level
    else if (/\/WARN\]:/.test(line)) {
        cls = 'mc-log-warn';
    }
    // DEBUG level - dim
    else if (/\/DEBUG\]:/.test(line)) {
        cls = 'mc-log-debug';
    }

    lastColorClass = cls;

    if (cls) {
        return `<span class="${cls}">${escaped}</span>`;
    }
    return escaped;
}

// -----------------------------------------------------------------------
// D-01: Stat card helpers (defined here to avoid cross-import from mc.ts)
// -----------------------------------------------------------------------

function fmtBytes(bytes: number): string {
    if (bytes >= 1_073_741_824) return (bytes / 1_073_741_824).toFixed(1) + ' GB';
    if (bytes >= 1_048_576)     return (bytes / 1_048_576).toFixed(0) + ' MB';
    return bytes + ' B';
}

function fmtUptime(ms: number): string {
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

/**
 * Live-update Phase 143 stat cards from WS stats event.
 * Targets .mc-resource-stats .mc-resource-row in order: CPU, RAM, Disk (SKIP), Uptime.
 * Disk (row[2]) is intentionally skipped — D-01 keeps disk cache-sourced.
 * cpu_absolute is ALREADY a percentage — do NOT multiply by 100 (Pitfall 5).
 */
function updateStatCards(
    cpuAbsolute: number,
    memoryBytes: number,
    memoryLimitBytes: number,
    uptimeMs: number,
): void {
    const stats = document.querySelector('.mc-resource-stats');
    if (!stats) return;

    const rows = stats.querySelectorAll<HTMLElement>('.mc-resource-row');
    if (rows.length < 4) return;

    // [0] CPU — cpu_absolute is already %, do NOT ×100
    const cpuVal = rows[0].querySelector<HTMLElement>('.fw-semibold');
    if (cpuVal) cpuVal.textContent = `${cpuAbsolute.toFixed(1)}%`;

    // [1] RAM
    const ramVal = rows[1].querySelector<HTMLElement>('.fw-semibold');
    if (ramVal) ramVal.textContent = `${fmtBytes(memoryBytes)} / ${fmtBytes(memoryLimitBytes)}`;

    // [2] Disk — intentionally skipped (D-01: disk stays cache-sourced on disconnect and live)

    // [3] Uptime
    const uptimeVal = rows[3].querySelector<HTMLElement>('.fw-semibold');
    if (uptimeVal) uptimeVal.textContent = fmtUptime(uptimeMs);
}

export class McConsole {
    private mintUrl: string = '';
    private ws: WebSocket | null = null;
    private wsBackoffMs: number = 1_000;
    // CR-01: track intentional closes and a single pending reconnect timer to
    // prevent the orphaned-timer / duplicate-socket storm on every reconnect cycle.
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private intentionalClose: boolean = false;
    private outputEl: HTMLPreElement;
    private container: HTMLElement;
    private bodyEl: HTMLElement;
    private badgeEl: HTMLElement | null;
    private badgeCountEl: HTMLElement | null;
    private statusEl: HTMLElement | null;
    private messageEl: HTMLElement | null;
    private newLinesCount: number = 0;
    private isFirstLoad: boolean = true;

    // Search state
    private searchInput: HTMLInputElement | null = null;
    private matchCountEl: HTMLElement | null = null;
    private matches: HTMLElement[] = [];
    private currentMatchIndex: number = -1;
    private searchQuery: string = '';
    private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    // WR-01: store the visibilitychange handler as an instance field so it can
    // be passed to removeEventListener in destroy() — anonymous closures cannot
    // be removed, causing listener leaks on every re-mint of McConsole.
    private readonly onVisibility = (): void => {
        if (document.hidden) {
            this.closeSocket();
        } else {
            void this.openSocket();
        }
    };

    constructor(section: HTMLElement) {
        this.container = section;
        this.outputEl = section.querySelector<HTMLPreElement>('.mc-console-output')!;
        this.bodyEl = section.querySelector<HTMLElement>('.mc-console-body')!;
        this.badgeEl = section.querySelector<HTMLElement>('.mc-console-new-lines');
        this.badgeCountEl = section.querySelector<HTMLElement>('.mc-console-new-lines-count');
        this.statusEl = section.querySelector<HTMLElement>('.mc-console-status');
        this.messageEl = section.querySelector<HTMLElement>('.mc-console-message');

        const streamUrl = section.dataset.streamUrl;
        if (!streamUrl) return;

        this.initWebSocket(streamUrl);
        this.initScrollPause();
        this.initCollapse();
        this.initResize();
        this.initSearch();
    }

    // -----------------------------------------------------------------------
    // WebSocket transport
    // -----------------------------------------------------------------------

    private initWebSocket(mintUrl: string): void {
        this.mintUrl = mintUrl;
        this.setStatus('connecting');
        this.openSocket();

        // D-03: disconnect when hidden, reconnect on visible
        // ONE listener only — replaces the old poll visibilitychange listener
        // WR-01: use named instance field so destroy() can removeEventListener
        document.addEventListener('visibilitychange', this.onVisibility);
    }

    private async openSocket(): Promise<void> {
        this.closeSocket();
        try {
            // WR-03 (Phase 259 code review): X-Requested-With is what Nette's Request::isAjax()
            // keys on, and McPresenter::load() branches on it -- with the header the expired-session
            // case returns D-12's 401 (which mc.ts's interceptor acts on); without it, load() took
            // the 302 branch, fetch followed the redirect transparently, resp.json() choked on the
            // login page's HTML, and the catch below swallowed it as a silent "connect error".
            // Every sibling MC module already sends it.
            const resp = await fetch(this.mintUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            if (!resp.ok) { this.handleConnectError(); return; }
            const data: { socket?: string; token?: string; error?: string } = await resp.json();
            if (data.error || !data.socket || !data.token) {
                this.handleMintError(data.error ?? 'unknown');
                return;
            }
            this.connectWs(data.socket, data.token);
        } catch {
            this.handleConnectError();
        }
    }

    /**
     * Connect to the panel-returned socket URL VERBATIM (D-06).
     * The socket is wss://node.ksat.ca:8443/api/servers/{uuid}/ws — trust as-is.
     */
    private connectWs(socket: string, token: string): void {
        this.ws = new WebSocket(socket);

        this.ws.onopen = () => {
            this.wsBackoffMs = 1_000; // reset backoff on successful connect
            this.send('auth', [token]);
        };

        this.ws.onmessage = (ev: MessageEvent) => {
            // WR-02: guard against truncated frames, proxy keepalives, or any
            // non-JSON control frame — parse failure drops the frame silently.
            let msg: { event: string; args?: string[] };
            try { msg = JSON.parse(ev.data as string); } catch { return; }
            switch (msg.event) {
                case 'auth success':
                    // Reset first-load: backlog burst is about to arrive (D-02 / Pitfall 8)
                    this.isFirstLoad = true;
                    this.setStatus('connected');
                    this.hideInlineMessage();
                    // WR-03: panel is confirmed reachable — clear the server-rendered
                    // stale notice so live data doesn't appear beneath "Panel offline".
                    this.clearStaleNotice();
                    // Request backlog (D-02) — send logs ensures we get the console history on reconnect
                    this.send('send logs', [null]);
                    break;
                case 'console output':
                    // CRITICAL (Pitfall 4): args[0] is PLAIN TEXT — do NOT JSON.parse it
                    this.handleConsoleOutput(msg.args?.[0] ?? '');
                    break;
                case 'stats':
                    // args[0] is a JSON string — JSON.parse is correct here
                    this.handleStats(msg.args?.[0] ?? '');
                    break;
                case 'status':
                    this.handleStatus(msg.args?.[0] ?? '');
                    break;
                case 'token expiring':
                    // Re-auth on same connection before token expires (D-05, Assumption A1)
                    void this.refreshToken();
                    break;
                case 'token expired':
                    // Missed the refresh window — re-mint from scratch
                    void this.openSocket();
                    break;
            }
        };

        this.ws.onclose = () => {
            // CR-01: intentional close (from closeSocket()) — reset flag and bail.
            // Do NOT schedule a reconnect; this was a deliberate teardown.
            if (this.intentionalClose) {
                this.intentionalClose = false;
                return;
            }
            // Only retry if tab is visible (D-03)
            if (!document.hidden) {
                this.setStatus('connecting');
                this.scheduleReconnect();
            }
        };

        this.ws.onerror = () => {
            // onclose fires after onerror — no duplicate retry needed here
            this.setStatus('error');
        };
    }

    /** Send a Pterodactyl WS event. Guard: only when connection is OPEN. */
    private send(event: string, args: (string | null)[]): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ event, args }));
        }
    }

    private closeSocket(): void {
        if (this.ws) {
            // CR-01: set flag BEFORE close() so the async onclose handler can detect
            // intentional closes and skip the reconnect path entirely.
            this.intentionalClose = true;
            this.ws.onclose = null;   // detach — stale socket must not schedule a reconnect
            this.ws.onerror = null;
            this.ws.close();
            this.ws = null;
        }
    }

    /**
     * Re-mint a fresh token and re-auth on the existing WS connection (D-05).
     * Silent on failure — 'token expired' will trigger a full openSocket() reconnect.
     */
    private async refreshToken(): Promise<void> {
        try {
            // WR-03 (Phase 259 code review) -- same isAjax() contract as openSocket() above. This
            // one is a periodic timer, so without the header every tick after session expiry also
            // took load()'s non-AJAX branch and added a storeRequest() session-section entry.
            const resp = await fetch(this.mintUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            if (!resp.ok) return;
            const data: { token?: string; socket?: string; error?: string } = await resp.json();
            if (data.token) {
                this.send('auth', [data.token]);
            }
        } catch { /* silent — token expired event will trigger full reconnect */ }
    }

    private scheduleReconnect(): void {
        // CR-01: single-timer guard — if a reconnect is already pending, do not
        // schedule a second one (prevents timer accumulation on every onclose event).
        if (this.reconnectTimer !== null) return;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            if (!document.hidden) void this.openSocket();
        }, this.wsBackoffMs);
        this.wsBackoffMs = Math.min(this.wsBackoffMs * 2, 30_000); // cap at 30 s
    }

    // -----------------------------------------------------------------------
    // WS event handlers
    // -----------------------------------------------------------------------

    /**
     * Handle a console output line.
     * CRITICAL (Pitfall 4): line is PLAIN TEXT, not JSON. Feed directly to appendContent.
     * On the first line after auth success (isFirstLoad), clear the output div first (D-02 / Pitfall 8).
     */
    private handleConsoleOutput(line: string): void {
        // Strip terminal escape codes the live Wings stream carries (this <pre> viewer is
        // not an xterm emulator) and trailing whitespace.
        let clean = stripAnsi(line).replace(/\s+$/, '');
        // Strip the leading JLine console prompt the modded server redraws before each
        // line — observed as ">...." ('>' + cursor dots), glued to the front of the log
        // text. Read-only (D-07): we never send input, so a leading '>' is always prompt
        // noise, never user content (logs start with '[', chat with '<'). Strip only the
        // prompt token (one optional trailing space) so content + indentation survive;
        // a line that is nothing but the prompt collapses to empty and is dropped.
        clean = clean.replace(/^>\.* ?/, '');
        if (!clean) return;
        if (this.isFirstLoad) {
            // First message in the backlog burst — start fresh
            this.isFirstLoad = false;
            this.outputEl.textContent = '';
            this.matches = [];
            this.currentMatchIndex = -1;
            this.updateMatchCount();
            // WR-05: reset the module-level color state so continuation lines at
            // the start of a fresh backlog burst don't inherit the color class from
            // a previous session's last line.
            lastColorClass = null;
        }
        const wasAtBottom = this.isAtBottom();
        this.appendContent(clean + '\n');
        if (wasAtBottom) {
            this.scrollToBottom();
        } else {
            this.newLinesCount++;
            this.updateBadge();
        }
    }

    /**
     * Handle a stats event.
     * args[0] IS a JSON string here (unlike console output — Pitfall 4).
     * cpu_absolute is ALREADY a percentage — do NOT ×100 (Pitfall 5).
     * disk_bytes is present but ignored — D-01 keeps disk cache-sourced.
     */
    private handleStats(rawJson: string): void {
        try {
            const s: {
                cpu_absolute: number;
                memory_bytes: number;
                memory_limit_bytes: number;
                uptime: number; // milliseconds
                state?: string;
                disk_bytes?: number;
            } = JSON.parse(rawJson);
            updateStatCards(s.cpu_absolute, s.memory_bytes, s.memory_limit_bytes, s.uptime);
        } catch { /* ignore malformed stats payload */ }
    }

    /**
     * Handle a status event (server state change).
     * Read-only signal (D-07).
     */
    private handleStatus(_state: string): void {
        this.hideInlineMessage();
    }

    /**
     * Handle a mint error (D-04 degraded states).
     * no_ptero_id → static "not available" message, no retry.
     * Anything else → "unavailable — retrying" with backoff reconnect.
     */
    private handleMintError(error: string): void {
        if (error === 'no_ptero_id') {
            this.showInlineMessage('Live console not available for this server.');
            // No retry — this server has no ptero_id
        } else {
            this.showInlineMessage('Console unavailable — retrying…');
            this.scheduleReconnect();
        }
    }

    /** Handle a connection/fetch error (D-04: unreachable panel or WS error). */
    private handleConnectError(): void {
        this.setStatus('error');
        this.scheduleReconnect();
    }

    // -----------------------------------------------------------------------
    // WR-03: clear server-rendered stale notice on first live update
    // -----------------------------------------------------------------------

    /**
     * Hide the server-rendered "Panel offline" stale notice once a live WS
     * event confirms the panel is reachable. Called on 'auth success'.
     * NOTE: if .mc-resource-stats was NOT rendered (server offline at page load),
     * updateStatCards() will no-op silently — a page reload is required to
     * reconcile the resources block in that case (documented limitation, D-01).
     */
    private clearStaleNotice(): void {
        const notice = document.querySelector<HTMLElement>('.mc-stale-notice');
        if (notice) notice.style.display = 'none';
    }

    // -----------------------------------------------------------------------
    // D-04 inline message slot
    // -----------------------------------------------------------------------

    private showInlineMessage(text: string): void {
        if (!this.messageEl) return;
        this.messageEl.textContent = text;
        this.messageEl.style.display = '';
    }

    private hideInlineMessage(): void {
        if (!this.messageEl) return;
        this.messageEl.style.display = 'none';
    }

    // -----------------------------------------------------------------------
    // Content append (search-aware) — UNCHANGED
    // -----------------------------------------------------------------------

    private appendContent(content: string): void {
        if (!this.searchQuery) {
            const lines = content.split('\n');
            // Remove trailing empty element from split
            if (lines.length > 0 && lines[lines.length - 1] === '') {
                lines.pop();
            }
            if (lines.length === 0) return;

            // Group lines into log entries (header + continuation lines)
            const groups: { header: string; continuations: string[] }[] = [];

            for (const line of lines) {
                if (LOG_LINE_START.test(line)) {
                    groups.push({ header: line, continuations: [] });
                } else if (groups.length > 0) {
                    groups[groups.length - 1].continuations.push(line);
                } else {
                    // Orphan continuation (split across chunks) — render standalone
                    // WR-04: SECURITY INVARIANT — class names passed to innerHTML here
                    // are HARD-CODED LITERALS from colorizeLine() only. NEVER interpolate
                    // log-derived strings into the markup string; escapeHtml does NOT
                    // escape quotes and cannot protect attribute injection.
                    const span = document.createElement('span');
                    span.innerHTML = colorizeLine(line) + '\n';
                    this.outputEl.appendChild(span);
                }
            }

            for (const group of groups) {
                if (group.continuations.length === 0) {
                    // Single-line entry
                    const span = document.createElement('span');
                    span.innerHTML = colorizeLine(group.header) + '\n';
                    this.outputEl.appendChild(span);
                } else {
                    // Multi-line entry — collapsible
                    const wrapper = document.createElement('span');
                    wrapper.className = 'mc-log-group mc-log-group--collapsed';

                    const headerSpan = document.createElement('span');
                    headerSpan.className = 'mc-log-group-header';
                    headerSpan.innerHTML = colorizeLine(group.header) + ' ';

                    const toggle = document.createElement('span');
                    toggle.className = 'mc-log-group-toggle';
                    toggle.textContent = `+${group.continuations.length}`;
                    headerSpan.appendChild(toggle);

                    const contSpan = document.createElement('span');
                    contSpan.className = 'mc-log-group-cont';
                    contSpan.innerHTML = '\n' + group.continuations.map(l => colorizeLine(l)).join('\n');

                    wrapper.appendChild(headerSpan);
                    wrapper.appendChild(contSpan);
                    wrapper.appendChild(document.createTextNode('\n'));

                    headerSpan.addEventListener('click', (e) => {
                        e.preventDefault();
                        wrapper.classList.toggle('mc-log-group--collapsed');
                    });

                    this.outputEl.appendChild(wrapper);
                }
            }
            return;
        }

        // Build nodes with highlights for new content (plain text mode for search)
        const fragment = this.buildHighlightedFragment(content);
        const newMarks = Array.from(fragment.querySelectorAll('.mc-console-highlight')) as HTMLElement[];
        this.outputEl.appendChild(fragment);
        this.matches.push(...newMarks);
        this.updateMatchCount();
    }

    private buildHighlightedFragment(text: string): DocumentFragment {
        const fragment = document.createDocumentFragment();
        const regex = new RegExp(escapeRegex(this.searchQuery), 'gi');
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = regex.exec(text)) !== null) {
            // Text before match
            if (match.index > lastIndex) {
                fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
            }
            // Match mark element
            const mark = document.createElement('mark');
            mark.className = 'mc-console-highlight';
            mark.textContent = match[0];
            fragment.appendChild(mark);
            lastIndex = regex.lastIndex;
        }

        // Remaining text after last match
        if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        return fragment;
    }

    // -----------------------------------------------------------------------
    // Status indicator — UNCHANGED
    // -----------------------------------------------------------------------

    private setStatus(state: 'connected' | 'connecting' | 'error'): void {
        if (!this.statusEl) return;

        this.statusEl.classList.remove(
            'mc-console-status--connected',
            'mc-console-status--connecting',
            'mc-console-status--error',
        );

        switch (state) {
            case 'connected':
                this.statusEl.classList.add('mc-console-status--connected');
                this.statusEl.textContent = 'Connected';
                break;
            case 'connecting':
                this.statusEl.classList.add('mc-console-status--connecting');
                this.statusEl.textContent = 'Reconnecting...';
                break;
            case 'error':
                this.statusEl.classList.add('mc-console-status--error');
                this.statusEl.textContent = 'Error';
                break;
        }
    }

    // -----------------------------------------------------------------------
    // Auto-scroll with pause/resume — UNCHANGED
    // -----------------------------------------------------------------------

    private isAtBottom(): boolean {
        return this.outputEl.scrollHeight - this.outputEl.scrollTop - this.outputEl.clientHeight < 10;
    }

    private scrollToBottom(): void {
        this.outputEl.scrollTop = this.outputEl.scrollHeight;
    }

    private initScrollPause(): void {
        this.outputEl.addEventListener('scroll', () => {
            if (this.isAtBottom()) {
                this.newLinesCount = 0;
                this.updateBadge();
            }
        });

        // Badge click: scroll to bottom and reset counter
        if (this.badgeEl) {
            this.badgeEl.addEventListener('click', () => {
                this.scrollToBottom();
                this.newLinesCount = 0;
                this.updateBadge();
            });
        }
    }

    // -----------------------------------------------------------------------
    // New lines badge — UNCHANGED
    // -----------------------------------------------------------------------

    private updateBadge(): void {
        if (!this.badgeEl) return;

        if (this.newLinesCount > 0) {
            this.badgeEl.style.display = '';
            if (this.badgeCountEl) {
                this.badgeCountEl.textContent = String(this.newLinesCount);
            }
        } else {
            this.badgeEl.style.display = 'none';
        }
    }

    // -----------------------------------------------------------------------
    // Search highlighting with match navigation — UNCHANGED
    // -----------------------------------------------------------------------

    private initSearch(): void {
        this.searchInput = this.container.querySelector<HTMLInputElement>('.mc-console-search-input');
        this.matchCountEl = this.container.querySelector<HTMLElement>('.mc-console-match-count');
        const prevBtn = this.container.querySelector<HTMLElement>('.mc-console-search-prev');
        const nextBtn = this.container.querySelector<HTMLElement>('.mc-console-search-next');

        if (!this.searchInput) return;

        this.searchInput.addEventListener('input', () => {
            if (this.searchDebounceTimer !== null) {
                clearTimeout(this.searchDebounceTimer);
            }
            this.searchDebounceTimer = setTimeout(() => {
                this.searchQuery = this.searchInput!.value;
                this.performSearch();
            }, 200);
        });

        this.searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.navigateMatch(e.shiftKey ? -1 : 1);
            }
        });

        if (prevBtn) {
            prevBtn.addEventListener('click', () => this.navigateMatch(-1));
        }
        if (nextBtn) {
            nextBtn.addEventListener('click', () => this.navigateMatch(1));
        }
    }

    private performSearch(): void {
        // Clear existing highlights: replace mark elements with their text content
        const existing = this.outputEl.querySelectorAll('.mc-console-highlight');
        existing.forEach((el) => {
            const parent = el.parentNode;
            if (parent) {
                parent.replaceChild(document.createTextNode(el.textContent || ''), el);
                parent.normalize();
            }
        });

        this.matches = [];
        this.currentMatchIndex = -1;

        if (!this.searchQuery) {
            this.updateMatchCount();
            return;
        }

        // Get full text, rebuild with highlights
        const fullText = this.outputEl.textContent || '';
        this.outputEl.textContent = '';
        const fragment = this.buildHighlightedFragment(fullText);
        this.outputEl.appendChild(fragment);

        this.matches = Array.from(this.outputEl.querySelectorAll('.mc-console-highlight')) as HTMLElement[];

        if (this.matches.length > 0) {
            this.currentMatchIndex = 0;
            this.matches[0].classList.add('mc-console-highlight--active');
            this.matches[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
        }

        this.updateMatchCount();
    }

    private navigateMatch(direction: 1 | -1): void {
        if (this.matches.length === 0) return;

        // Remove active from current
        if (this.currentMatchIndex >= 0 && this.currentMatchIndex < this.matches.length) {
            this.matches[this.currentMatchIndex].classList.remove('mc-console-highlight--active');
        }

        // Calculate new index with wrapping
        this.currentMatchIndex = (this.currentMatchIndex + direction + this.matches.length) % this.matches.length;

        // Add active to new current
        this.matches[this.currentMatchIndex].classList.add('mc-console-highlight--active');
        this.matches[this.currentMatchIndex].scrollIntoView({ block: 'center', behavior: 'smooth' });

        this.updateMatchCount();
    }

    private updateMatchCount(): void {
        if (!this.matchCountEl) return;

        if (this.matches.length === 0 && !this.searchQuery) {
            this.matchCountEl.textContent = '';
        } else if (this.matches.length === 0) {
            this.matchCountEl.textContent = '0/0';
        } else {
            this.matchCountEl.textContent = `${this.currentMatchIndex + 1}/${this.matches.length}`;
        }
    }

    // -----------------------------------------------------------------------
    // Collapse/expand — UNCHANGED
    // -----------------------------------------------------------------------

    private initCollapse(): void {
        const btn = this.container.querySelector<HTMLElement>('.mc-console-collapse-btn');
        if (!btn) return;

        btn.addEventListener('click', () => {
            this.container.classList.toggle('mc-console-section--collapsed');
            // WS stays active when collapsed (per D-03: only tab hidden closes the socket)
        });
    }

    // -----------------------------------------------------------------------
    // Resize via drag handle — UNCHANGED
    // -----------------------------------------------------------------------

    private initResize(): void {
        const handle = this.container.querySelector<HTMLElement>('.mc-console-resize-handle');
        if (!handle) return;

        const maxPx = (): number => window.innerHeight * 0.8;

        const onPointerDown = (startY: number): void => {
            const startHeight = this.bodyEl.offsetHeight;

            const onMove = (currentY: number): void => {
                const delta = currentY - startY;
                const newHeight = Math.min(Math.max(startHeight + delta, 150), maxPx());
                this.bodyEl.style.height = `${newHeight}px`;
            };

            const onMouseMove = (e: MouseEvent): void => onMove(e.clientY);
            const onTouchMove = (e: TouchEvent): void => {
                if (e.touches.length > 0) onMove(e.touches[0].clientY);
            };

            const cleanup = (): void => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', cleanup);
                document.removeEventListener('touchmove', onTouchMove);
                document.removeEventListener('touchend', cleanup);
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', cleanup);
            document.addEventListener('touchmove', onTouchMove);
            document.addEventListener('touchend', cleanup);
        };

        handle.addEventListener('mousedown', (e: MouseEvent) => {
            e.preventDefault();
            onPointerDown(e.clientY);
        });

        handle.addEventListener('touchstart', (e: TouchEvent) => {
            if (e.touches.length > 0) {
                onPointerDown(e.touches[0].clientY);
            }
        }, { passive: true });
    }

    // -----------------------------------------------------------------------
    // Cleanup
    // -----------------------------------------------------------------------

    destroy(): void {
        this.closeSocket();
        // CR-01: cancel any pending reconnect timer so a destroyed instance cannot
        // open a new socket after destroy() returns.
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.searchDebounceTimer !== null) {
            clearTimeout(this.searchDebounceTimer);
        }
        // WR-01: remove the visibilitychange listener so a destroyed instance
        // cannot re-open a socket on tab-focus after destroy().
        document.removeEventListener('visibilitychange', this.onVisibility);
    }
}
